import { X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ChangeEvent, type FocusEvent } from "react";
import { convertir, formatear, formatearNumero, parsearImporte, type Moneda } from "@tora/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { SelectorMoneda } from "./switch-moneda";

export interface InputImporteProps {
  /** Valor numerico. `null` = vacio o no interpretable. NUNCA 0 por defecto. */
  valor: number | null;
  onValorChange: (valor: number | null) => void;
  moneda: Moneda;
  /** Si se omite, la moneda queda fija y el selector no se dibuja. */
  onMonedaChange?: (moneda: Moneda) => void;
  /**
   * Cotizacion (ARS por 1 USD) para convertir el numero ya tipeado cuando se
   * cambia de moneda. Sin esto el selector solo cambia la etiqueta.
   *
   * Se pasa desde afuera A PROPOSITO, y no se lee del `MonedaProvider`: ese
   * contexto arranca en 1 y nadie lo conecta nunca al backend (ver el comentario
   * de `BarraEstado`). Convertir con esa cotizacion no daria error: dejaria el
   * mismo numero, que es la falla mas dificil de ver.
   */
  cotizacion?: number;
  /**
   * Apaga la conversion aunque haya cotizacion.
   *
   * Se usa donde el campo TRANSCRIBE un documento: en una linea de remito,
   * "3.000" que estaba en la columna equivocada sigue siendo 3.000 cuando se
   * corrige la moneda, no US$ 2,07.
   */
  convertirAlCambiarMoneda?: boolean;
  label?: string;
  placeholder?: string;
  ayuda?: string;
  disabled?: boolean;
  requerido?: boolean;
  name?: string;
  id?: string;
  autoFocus?: boolean;
  className?: string;
  /**
   * Avisa cuando el campo entra o sale del estado de error. `onValorChange` emite
   * `null` tanto para "vacio" como para "no se entiende"; un formulario con campos
   * de plata OPCIONALES necesita esto para no tratar un texto ilegible como
   * "sin importe" y seguir de largo.
   */
  onInvalidoChange?: (invalido: boolean) => void;
  /**
   * Un negativo es un error, no un importe. Sin esto el campo aceptaba
   * «-5.000,00», el consumidor lo descartaba en silencio y la pantalla mostraba
   * dos precios que se contradecian (el precio del ticket de venta).
   */
  sinNegativos?: boolean;
  /**
   * La ✕ de adentro del campo para vaciarlo. Se apaga donde al lado ya hay
   * otra ✕ (quitar un medio de pago): dos iguales pegadas se confunden.
   */
  vaciable?: boolean;
}

const MENSAJE_NO_PARSEA =
  "No se entiende como importe. Escribilo asi: 1.234,56 (punto para miles, coma para decimales).";
const MENSAJE_REQUERIDO = "Ingresa un importe.";
const MENSAJE_NEGATIVO = "No puede ser negativo.";

/**
 * Campo de carga de plata.
 *
 * Reglas duras (MASTER.md seccion 7):
 *
 *  - NUNCA un 0 silencioso. Si el texto no parsea, el campo emite `null`, se marca
 *    `aria-invalid` y muestra el error. Un cero fantasma en un asiento es peor que
 *    un formulario que no deja guardar.
 *  - No usa `type="number"`: no acepta el formato argentino "1.234,56" y la rueda
 *    del mouse cambia montos por accidente.
 *  - El parseo lo hace `parsearImporte()` de `@tora/shared`, la misma funcion que
 *    usa el backend. No hay una segunda implementacion del formato argentino.
 *  - La moneda se elige AL LADO del campo, y el eco de confirmacion se muestra ya
 *    formateado en esa moneda.
 */
export function InputImporte({
  valor,
  onValorChange,
  moneda,
  onMonedaChange,
  cotizacion,
  convertirAlCambiarMoneda = true,
  label,
  placeholder = "0,00",
  ayuda,
  disabled = false,
  requerido = false,
  name,
  id,
  autoFocus = false,
  className,
  onInvalidoChange,
  sinNegativos = false,
  vaciable = true,
}: InputImporteProps): JSX.Element {
  const idAuto = useId();
  const inputId = id ?? `importe-${idAuto}`;
  const errorId = `${inputId}-error`;
  const ecoId = `${inputId}-eco`;

  const [texto, setTexto] = useState<string>(() => (valor === null ? "" : formatearNumero(valor)));
  const [error, setError] = useState<string | null>(null);
  const editando = useRef(false);
  const campo = useRef<HTMLInputElement>(null);
  /**
   * Si alguien escribió en el campo desde que se cargó el valor. El «Ingresá un
   * importe» al salir vacío es para quien borró lo que había, no para un campo
   * que nadie tocó: al reabrir un diálogo con autoFocus, el foco se pierde solo
   * (un toast, el cierre del diálogo anterior) y el campo aparecía en rojo
   * antes de que el usuario hiciera nada.
   */
  const tocado = useRef(false);
  /** El ultimo cambio de moneda, para poder deshacerlo sin perder centavos. */
  const ida = useRef<{ desde: Moneda; valor: number; convertido: number } | null>(null);

  // Si el valor cambia desde afuera (reset del form, carga de un registro), se
  // resincroniza el texto. Mientras el usuario tipea no se pisa lo que escribio.
  useEffect(() => {
    if (editando.current) return;
    setTexto(valor === null ? "" : formatearNumero(valor));
    setError(null);
    tocado.current = false;
  }, [valor]);

  function alCambiar(e: ChangeEvent<HTMLInputElement>): void {
    const nuevo = e.target.value;
    editando.current = true;
    tocado.current = true;
    setTexto(nuevo);

    if (nuevo.trim() === "") {
      setError(requerido ? MENSAJE_REQUERIDO : null);
      onValorChange(null);
      return;
    }

    const parseado = parsearImporte(nuevo);
    if (parseado === null) {
      setError(MENSAJE_NO_PARSEA);
      onValorChange(null); // <- explicitamente null, no 0.
      return;
    }
    if (sinNegativos && parseado < 0) {
      setError(MENSAJE_NEGATIVO);
      onValorChange(null);
      return;
    }

    setError(null);
    ida.current = null;
    onValorChange(parseado);
  }

  /**
   * La ✕ de adentro del campo: lo deja vacío de un toque y con el foco puesto,
   * listo para escribir. En el celular borrar un importe largo era tocar la
   * tecla de borrar diez veces.
   */
  function vaciar(): void {
    editando.current = true;
    tocado.current = true;
    ida.current = null;
    setTexto("");
    setError(requerido ? MENSAJE_REQUERIDO : null);
    onValorChange(null);
    campo.current?.focus();
  }

  /**
   * Al entrar, el `Input` de abajo marca todo el número (es un campo
   * `inputMode="decimal"`), así lo que se escribe lo reemplaza.
   */
  function alEntrar(): void {
    editando.current = true;
  }

  function alSalir(e: FocusEvent<HTMLInputElement>): void {
    editando.current = false;
    const crudo = e.target.value;
    if (crudo.trim() === "") {
      setError(requerido && tocado.current ? MENSAJE_REQUERIDO : null);
      return;
    }
    const parseado = parsearImporte(crudo);
    if (parseado === null) {
      setError(MENSAJE_NO_PARSEA);
      return;
    }
    if (sinNegativos && parseado < 0) {
      setError(MENSAJE_NEGATIVO);
      return;
    }
    // Normaliza lo tipeado al formato argentino canonico.
    setTexto(formatearNumero(parseado));
    // Lo que queda a la vista es lo que vale. Si el formulario perdio el
    // numero mientras se tipeaba (un reset desde afuera no pisa el texto en
    // edicion), se lo devuelve: un campo que muestra 50.000 y por dentro esta
    // vacio deja el boton de seguir apagado sin decir por que.
    if (parseado !== valor) onValorChange(parseado);
  }

  /**
   * El selector de moneda no es una etiqueta: es un conversor.
   *
   * Tipear 3.000 pensando en pesos y despues pasar a dolares tiene que dar
   * US$ 2,07, no US$ 3.000. La otra lectura convertia un accesorio en un iPhone
   * de un clic, y el error solo se veia al mirar el precio final.
   *
   * Si no le pasaron cotizacion o el campo esta vacio, no hay nada que
   * convertir y el selector solo cambia la moneda, como antes.
   */
  function alCambiarMoneda(nueva: Moneda): void {
    if (!onMonedaChange || nueva === moneda) return;

    const hayCotizacion = typeof cotizacion === "number" && cotizacion > 0;
    if (convertirAlCambiarMoneda && hayCotizacion && valor !== null) {
      editando.current = false;
      setError(null);

      // Ida y vuelta tiene que devolver el numero exacto. Convertir dos veces
      // pierde centavos ($ 19.338 -> US$ 16,12 -> $ 19.344), y un costo que se
      // mueve solo porque alguien toco el selector dos veces es plata que
      // cambia sin que nadie lo haya decidido. Si vuelve a la moneda de donde
      // salio y nadie edito el numero en el medio, se restaura el original.
      const vuelta = ida.current;
      if (vuelta && vuelta.desde === nueva && vuelta.convertido === valor) {
        ida.current = null;
        onValorChange(vuelta.valor);
        onMonedaChange(nueva);
        return;
      }

      const convertido = convertir(valor, moneda, nueva, cotizacion);
      ida.current = { desde: moneda, valor, convertido };
      onValorChange(convertido);
    }

    onMonedaChange(nueva);
  }

  const hayError = error !== null;
  // Se avisa por efecto y no dentro de cada handler: asi el consumidor recibe un
  // solo aviso por transicion, incluso cuando el error lo pone alSalir.
  const avisarInvalido = useRef(onInvalidoChange);
  avisarInvalido.current = onInvalidoChange;
  useEffect(() => {
    avisarInvalido.current?.(hayError);
  }, [hayError]);
  // El eco confirma COMO se interpreto lo tipeado, asi que solo tiene sentido
  // mientras el texto y el formato canonico difieren ("1705000" -> "1.705.000,00").
  // Una vez normalizado el campo, repetirlo abajo era el mismo numero dos veces:
  // ruido, y una linea de alto que descolocaba las filas de cobro.
  const mostrarEco = !hayError && valor !== null && texto.trim() !== formatearNumero(valor);
  // Una vez que el eco aparecio, su renglon queda reservado aunque se apague.
  // El eco se apaga al salir del campo, y salir es justo el mousedown sobre el
  // boton de abajo: el boton subia un renglon antes del mouseup y el clic se
  // perdia («Aplicar la compra», «Guardar el encabezado»). No se reserva desde
  // el principio para no agregarle un renglon vacio a cada campo sin tocar.
  const [renglonReservado, setRenglonReservado] = useState(false);
  if (mostrarEco && !renglonReservado) setRenglonReservado(true);
  // El último eco que se mostró: cuando se apaga, el renglón queda con ese
  // texto pero invisible (ver abajo por qué no se saca).
  const textoEco = mostrarEco && valor !== null ? `= ${formatear(valor, moneda)} ${moneda}` : null;
  const [ecoVisto, setEcoVisto] = useState("");
  if (textoEco !== null && textoEco !== ecoVisto) setEcoVisto(textoEco);

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label ? (
        <Label htmlFor={inputId}>
          {label}
          {requerido ? (
            <span className="ml-1 text-destructive" aria-hidden="true">
              *
            </span>
          ) : null}
        </Label>
      ) : null}

      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium tabular-nums",
              moneda === "ARS" ? "text-ars" : "text-usd",
            )}
          >
            {moneda === "ARS" ? "$" : "US$"}
          </span>
          <Input
            ref={campo}
            id={inputId}
            name={name}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={disabled}
            value={texto}
            placeholder={placeholder}
            aria-invalid={hayError}
            aria-describedby={hayError ? errorId : mostrarEco ? ecoId : undefined}
            onChange={alCambiar}
            onBlur={alSalir}
            onFocus={alEntrar}
            className={cn(
              "text-right font-medium tabular-nums",
              moneda === "ARS" ? "pl-9" : "pl-12",
              vaciable && texto !== "" && !disabled && "pr-10",
            )}
          />
          {vaciable && texto !== "" && !disabled ? (
            <button
              type="button"
              // El nombre no repite el del campo: si no, buscar el campo por su
              // etiqueta encuentra dos cosas (el campo y esta ✕).
              aria-label="Borrar el número"
              // Sin esto el toque saca el foco del campo antes del clic, y al
              // salir se normaliza y se valida un texto que se iba a borrar.
              onMouseDown={(e) => e.preventDefault()}
              onClick={vaciar}
              className="absolute right-1 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        {/* El selector aparece SOLO si la moneda se puede elegir.
            Cuando viene impuesta desde afuera —la caja del cobro decide si el
            importe es en pesos o en dolares— dibujar un selector deshabilitado
            era peor que no dibujarlo: ocupaba mas de la mitad del ancho, en el
            ticket de venta dejaba al campo con espacio para cero digitos, y los
            numeros que se tipeaban no se veian. La moneda ya la comunica el
            prefijo $ / US$ del propio campo, con su color. */}
        {onMonedaChange ? (
          <SelectorMoneda
            valor={moneda}
            onChange={alCambiarMoneda}
            disabled={disabled}
            tamano="compacto"
            etiqueta={label ? `Moneda de ${label}` : "Moneda del importe"}
          />
        ) : null}
      </div>

      {/*
        EL RENGLÓN DE ABAJO NUNCA SE SACA DE LA PÁGINA
        ----------------------------------------------
        El eco se apaga al salir del campo, y antes se lo sacaba: justo en ese
        instante el foco está en el aire (entre este campo y el que se tocó), y
        el cuadro de Radix, al ver que desaparece algo con el foco en el aire,
        se queda con el foco. El campo tocado nunca lo recibía: en el alta un
        producto quedó sin costo. Ahora el renglón, una vez que apareció, se
        queda y solo se vuelve invisible; y la ayuda tiene su propio renglón,
        que no se alterna con el eco.
      */}
      {hayError ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
      {renglonReservado ? (
        <p
          id={ecoId}
          aria-hidden={!mostrarEco || undefined}
          data-renglon-reservado={!mostrarEco ? "" : undefined}
          className={cn(
            "h-4 text-xs tabular-nums",
            moneda === "ARS" ? "text-ars" : "text-usd",
            (!mostrarEco || hayError) && "invisible",
          )}
        >
          {ecoVisto}
        </p>
      ) : null}
      {ayuda ? <p className="text-xs text-muted-foreground">{ayuda}</p> : null}
    </div>
  );
}
